"use client";

import GlowBusinessMapEditor from "@/components/glow-business-map-editor";

import GlowOrb from "@/components/glow-orb";

type GlowBusinessMapProps = {
  businessMap: any;
  preview?: boolean;
  confirming?: boolean;
  error?: string;
  onConfirm?: () => void | Promise<void>;
  onSave?: (nextBusinessMap: any) => void | Promise<void>;
};

function safeArray(value: unknown): any[] {
  return Array.isArray(value) ? value : [];
}

function safeUrl(value: unknown): string {
  if (typeof value !== "string") {
    return "";
  }

  const trimmed = value.trim();

  return trimmed.startsWith("https://") || trimmed.startsWith("http://")
    ? trimmed
    : "";
}

function statusDetails(status: string, ownerConfirmed: boolean) {
  if (
    ownerConfirmed ||
    status === "confirmed_by_you"
  ) {
    return {
      label: "Confirmed by you",
      className: "border-[#b8dfd2] bg-[#eaf8f3] text-[#007c7a]",
    };
  }

  if (status === "needs_confirmation") {
    return {
      label: "Needs confirmation",
      className: "border-[#f2c9b9] bg-[#fff5f1] text-[#bd4426]",
    };
  }

  return {
    label: "Found by Glow",
    className: "border-[#b9e3de] bg-[#effbf9] text-[#007c7a]",
  };
}

function StatusBadge({
  status,
  ownerConfirmed,
}: {
  status?: string;
  ownerConfirmed: boolean;
}) {
  const resolvedStatus = status || "found_by_glow";
  const details = statusDetails(resolvedStatus, ownerConfirmed);

  const dotClassName = ownerConfirmed
    ? "bg-[#007c7a]"
    : resolvedStatus === "needs_confirmation"
      ? "bg-[#ff5a3d]"
      : "bg-[#20b7ad]";

  return (
    <span
      className={`inline-flex max-w-full items-center gap-2 self-start whitespace-nowrap rounded-full border px-3 py-1.5 text-[10px] font-normal uppercase leading-none tracking-[0.08em] ${details.className}`}
    >
      <span
        className={`h-1.5 w-1.5 shrink-0 rounded-full ${dotClassName}`}
        aria-hidden="true"
      />

      <span>{details.label}</span>
    </span>
  );
}

export default function GlowBusinessMap({
  businessMap,
  preview = false,
  confirming = false,
  error = "",
  onConfirm,
  onSave,
}: GlowBusinessMapProps) {
  if (!businessMap || typeof businessMap !== "object") {
    return null;
  }

  const salesChannels = safeArray(businessMap.salesChannels);
  const offerTypes = safeArray(businessMap.offerTypes);
  const idealCustomers = safeArray(businessMap.idealCustomers);
  const keyLinks = safeArray(businessMap.keyLinks);
  const ownerQuestions = safeArray(businessMap.ownerQuestions);
  const physicalCharacteristics = safeArray(
    businessMap.physicalPresence?.characteristics,
  );

  const ownerConfirmed = businessMap.ownerConfirmed === true;
  const accomplishment = businessMap.accomplishment || {};
  const ahaOpportunity = businessMap.ahaOpportunity || {};

  const channelsMapped =
    typeof accomplishment.channelsMapped === "number"
      ? accomplishment.channelsMapped
      : salesChannels.length;

  const linksSaved =
    typeof accomplishment.linksSaved === "number"
      ? accomplishment.linksSaved
      : keyLinks.length;

  const customerGroupsIdentified =
    typeof accomplishment.customerGroupsIdentified === "number"
      ? accomplishment.customerGroupsIdentified
      : idealCustomers.length;

  const offersIdentified =
    typeof accomplishment.offersIdentified === "number"
      ? accomplishment.offersIdentified
      : offerTypes.length;

  const opportunitiesFound =
    typeof accomplishment.opportunitiesFound === "number"
      ? accomplishment.opportunitiesFound
      : ahaOpportunity?.title
        ? 1
        : 0;

  return (
    <section
      id="glow-business-map"
      className={`overflow-hidden rounded-[2rem] border border-[#bcd0ca] bg-white shadow-[0_20px_55px_rgba(9,39,35,0.10)] ${
        preview ? "mt-6" : "mb-6"
      }`}
    >
      <div className="bg-gradient-to-br from-[#092723] via-[#113d37] to-[#007c7a] p-6 text-white md:p-8">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-sm font-light uppercase tracking-[0.2em] text-[#61e6dc]">
              Your Glow Business Map
            </p>

            <h2 className="mt-2 max-w-4xl text-3xl font-light tracking-tight md:text-4xl">
              Glow has mapped how your business works.
            </h2>

            <p className="mt-4 max-w-4xl text-sm font-light leading-7 text-[#d7e5df] md:text-base">
              {businessMap.summary ||
                "Glow has prepared a working map of how your business sells, serves customers and creates value."}
            </p>
          </div>

          <div className="flex items-center gap-4 self-start">
            <GlowOrb
              size="sm"
              spinning={!ownerConfirmed}
              label={ownerConfirmed ? "Glow Business Map confirmed" : "Glow mapped this business"}
            />
            <StatusBadge
              status={ownerConfirmed ? "confirmed_by_you" : "found_by_glow"}
              ownerConfirmed={ownerConfirmed}
            />
          </div>
        </div>

        <div className="mt-7 grid grid-cols-2 gap-3 md:grid-cols-5">
          {[
            ["Sales channels", channelsMapped],
            ["Important links", linksSaved],
            ["Customer groups", customerGroupsIdentified],
            ["Offers identified", offersIdentified],
            ["Opportunities", opportunitiesFound],
          ].map(([label, value]) => (
            <div
              key={String(label)}
              className="rounded-3xl border border-white/10 bg-white/[0.07] p-4"
            >
              <p className="text-2xl font-light text-[#61e6dc]">{value}</p>
              <p className="mt-1 text-xs font-light leading-5 text-[#d7e5df]">
                {label}
              </p>
            </div>
          ))}
        </div>
      </div>

      {ahaOpportunity?.title && (
        <div className="border-b border-[#d7e5df] bg-[#fff5f1] p-6 md:p-8">
          <p className="text-sm font-light uppercase tracking-[0.18em] text-[#ff5a3d]">
            Glow found something useful
          </p>

          <h3 className="mt-2 text-2xl font-light text-[#092723]">
            {ahaOpportunity.title}
          </h3>

          {ahaOpportunity.detail && (
            <p className="mt-3 max-w-4xl text-sm font-light leading-7 text-[#52635f]">
              {ahaOpportunity.detail}
            </p>
          )}

          {ahaOpportunity.whyItMatters && (
            <p className="mt-3 max-w-4xl text-sm font-normal leading-7 text-[#092723]">
              Why it matters: {ahaOpportunity.whyItMatters}
            </p>
          )}
        </div>
      )}

      <div className="p-6 md:p-8">
        <div className="grid gap-4 md:grid-cols-2">
          {businessMap.sellingModel?.value && (
            <div className="rounded-3xl border border-[#d7e5df] bg-[#f4fbf8] p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <p className="text-sm font-normal text-[#092723]">
                  How you sell
                </p>

                <StatusBadge
                  status={businessMap.sellingModel.status}
                  ownerConfirmed={ownerConfirmed}
                />
              </div>

              <p className="mt-3 text-lg font-light leading-7 text-[#007c7a]">
                {businessMap.sellingModel.value}
              </p>

              {businessMap.sellingModel.evidence && (
                <p className="mt-3 text-xs font-light leading-5 text-[#52635f]">
                  {businessMap.sellingModel.evidence}
                </p>
              )}
            </div>
          )}

          {businessMap.physicalPresence?.value && (
            <div className="rounded-3xl border border-[#d7e5df] bg-[#fbf8f3] p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <p className="text-sm font-normal text-[#092723]">
                  Physical presence
                </p>

                <StatusBadge
                  status={businessMap.physicalPresence.status}
                  ownerConfirmed={ownerConfirmed}
                />
              </div>

              <p className="mt-3 text-lg font-light leading-7 text-[#092723]">
                {businessMap.physicalPresence.value}
              </p>

              {physicalCharacteristics.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {physicalCharacteristics.map(
                    (characteristic: string, index: number) => (
                      <span
                        key={`${characteristic}-${index}`}
                        className="rounded-full bg-white px-3 py-2 text-xs font-light text-[#52635f]"
                      >
                        {characteristic}
                      </span>
                    ),
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {salesChannels.length > 0 && (
          <div className="mt-5 rounded-3xl border border-[#d7e5df] p-5">
            <p className="text-sm font-normal text-[#092723]">
              Sales and booking channels Glow found
            </p>

            <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {salesChannels.map((channel: any, index: number) => {
                const url = safeUrl(channel?.url);

                return (
                  <div
                    key={`${channel?.name || "channel"}-${index}`}
                    className="min-w-0 rounded-3xl border border-[#dcebe6] bg-[#f4fbf8] p-5"
                  >
                    <div className="min-w-0">
                      <p className="break-words text-base font-normal leading-6 text-[#092723]">
                        {channel?.name || "Sales channel"}
                      </p>

                      <div className="mt-3">
                        <StatusBadge
                          status={channel?.status}
                          ownerConfirmed={ownerConfirmed}
                        />
                      </div>
                    </div>

                    {channel?.type && (
                      <p className="mt-4 text-xs font-light uppercase tracking-[0.12em] text-[#007c7a]">
                        {String(channel.type).replaceAll("_", " ")}
                      </p>
                    )}

                    {channel?.evidence && (
                      <p className="mt-2 text-xs font-light leading-5 text-[#52635f]">
                        {channel.evidence}
                      </p>
                    )}

                    {url && (
                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-3 inline-block text-xs font-normal text-[#007c7a] underline decoration-[#61e6dc] underline-offset-4"
                      >
                        Open link
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {!preview && offerTypes.length > 0 && (
          <div className="mt-5 rounded-3xl border border-[#d7e5df] p-5">
            <p className="text-sm font-normal text-[#092723]">
              What your business sells
            </p>

            <div className="mt-4 flex flex-wrap gap-3">
              {offerTypes.map((offer: any, index: number) => (
                <div
                  key={`${offer?.name || "offer"}-${index}`}
                  className="rounded-2xl bg-[#fbf8f3] px-4 py-3"
                >
                  <p className="text-sm font-light text-[#092723]">
                    {offer?.name || "Offer"}
                  </p>

                  {offer?.type && (
                    <p className="mt-1 text-xs font-light text-[#52635f]">
                      {String(offer.type).replaceAll("_", " ")}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {!preview && idealCustomers.length > 0 && (
          <div className="mt-5 rounded-3xl border border-[#d7e5df] p-5">
            <p className="text-sm font-normal text-[#092723]">
              Likely ideal customers
            </p>

            <p className="mt-2 text-xs font-light leading-5 text-[#52635f]">
              Glow treats these as informed suggestions until you confirm them.
            </p>

            <div className="mt-4 grid gap-3 md:grid-cols-3">
              {idealCustomers.map((customer: any, index: number) => (
                <div
                  key={`${customer?.label || "customer"}-${index}`}
                  className="rounded-2xl bg-[#fff5f1] p-4"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <p className="text-sm font-normal text-[#092723]">
                      {customer?.label || "Customer group"}
                    </p>

                    <StatusBadge
                      status={customer?.status || "needs_confirmation"}
                      ownerConfirmed={ownerConfirmed}
                    />
                  </div>

                  {customer?.reason && (
                    <p className="mt-3 text-xs font-light leading-5 text-[#52635f]">
                      {customer.reason}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {!preview && keyLinks.length > 0 && (
          <div className="mt-5 rounded-3xl border border-[#d7e5df] p-5">
            <p className="text-sm font-normal text-[#092723]">
              Important business links
            </p>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {keyLinks.map((link: any, index: number) => {
                const url = safeUrl(link?.url);

                return (
                  <div
                    key={`${link?.label || "link"}-${index}`}
                    className="flex items-center justify-between gap-3 rounded-2xl bg-[#f4fbf8] p-4"
                  >
                    <div>
                      <p className="text-sm font-light text-[#092723]">
                        {link?.label || "Business link"}
                      </p>

                      {link?.type && (
                        <p className="mt-1 text-xs font-light text-[#52635f]">
                          {String(link.type).replaceAll("_", " ")}
                        </p>
                      )}
                    </div>

                    {url && (
                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="shrink-0 rounded-xl border border-[#bcd0ca] bg-white px-3 py-2 text-xs font-normal text-[#007c7a]"
                      >
                        Open
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {!preview && ownerQuestions.length > 0 && !ownerConfirmed && (
          <div className="mt-5 rounded-3xl border border-[#f0d8cf] bg-[#fffaf8] p-5">
            <p className="text-sm font-normal text-[#092723]">
              What Glow will confirm with you next
            </p>

            <div className="mt-4 grid gap-3 md:grid-cols-3">
              {ownerQuestions.map((question: string, index: number) => (
                <div
                  key={`${question}-${index}`}
                  className="rounded-2xl bg-white p-4 text-sm font-light leading-6 text-[#52635f]"
                >
                  {question}
                </div>
              ))}
            </div>
          </div>
        )}

        {!preview && (
          <div className="mt-6 flex flex-col gap-3 rounded-3xl bg-[#092723] p-5 text-white sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-normal">
                {ownerConfirmed
                  ? "Your Business Map is confirmed."
                  : "Does this feel like a good map of your business?"}
              </p>

              <p className="mt-1 text-xs font-light leading-5 text-[#d7e5df]">
                {ownerConfirmed
                  ? "Glow will use it as business memory across future work."
                  : "Confirming it helps every Glow agent produce more relevant work."}
              </p>
            </div>

            {!ownerConfirmed && onConfirm && (
              <button
                type="button"
                onClick={() => void onConfirm()}
                disabled={confirming}
                className="shrink-0 rounded-2xl bg-[#61e6dc] px-5 py-3 text-sm font-normal text-[#092723] transition hover:bg-[#84eee6] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {confirming
                  ? "Saving confirmation..."
                  : "Yes, this understands my business"}
              </button>
            )}
          </div>
        )}

        {!preview && onSave && (
          <GlowBusinessMapEditor
            businessMap={businessMap}
            onSave={onSave}
          />
        )}

        {error && (
          <p className="mt-4 rounded-2xl bg-[#fff1ec] p-4 text-sm font-light text-[#bd4426]">
            {error}
          </p>
        )}
      </div>
    </section>
  );
}
